import { DOCUMENT, KeyValue } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  HostBinding,
  inject,
  Input,
  OnChanges,
  Signal,
} from '@angular/core';
import { FlowNode, SocketType } from '@features/flow-ui-creator/interfaces/flow-ui.interface';
import { DebugHelperService } from '@shared/services/debug-helper.service';
import { NodeType } from '../../../interfaces/node.interface';
import { FlowService } from '../../../services/flow.service';

@Component({
  template: '',
})
export class BaseNodeComponent<F extends FlowNode = FlowNode> implements OnChanges {
  @Input()
  data!: F;

  @Input()
  emit!: (data: any) => void;

  @Input()
  rendered!: () => void;

  socketType = SocketType;
  seed = 0;
  nodeTypeEnum = NodeType;
  excludedDoubleClickNodes = [NodeType.End, NodeType.Terminate, NodeType.Gateway];

  protected readonly showIds: Signal<boolean>;
  protected cdr = inject(ChangeDetectorRef);
  protected flowService = inject(FlowService);

  readonly #document = inject(DOCUMENT);
  readonly #debugHelperService = inject(DebugHelperService);
  #appendTo = computed(() => {
    const flowEditorContentElement = this.#document.getElementById('flowEditor') ?? 'body';

    return !this.flowService.isFullscreen() ? 'body' : flowEditorContentElement;
  });

  constructor() {
    this.cdr.detach();
    this.showIds = this.#debugHelperService.shouldShowIds;

    effect(() => {
      if (this.showIds() || this.showIds() === false) {
        this.cdr.detectChanges();
      }
    });
  }

  @HostBinding('class.selected')
  get selected(): boolean {
    return this.data.selected;
  }

  @HostBinding('class.neqto-node')
  get neqtoNodeClass(): boolean {
    return true;
  }

  @HostBinding('attr.data-node-id')
  get nodeId(): string {
    return `node${this.data.id}`;
  }

  ngOnChanges(): void {
    this.cdr.detectChanges();
    requestAnimationFrame(() => {
      return this.rendered();
    });
    this.seed++;
  }

  sortByIndex<N extends object, T extends KeyValue<string, N & { index?: number }>>(a: T, b: T) {
    const ai = a.value.index || 0;
    const bi = b.value.index || 0;

    return ai - bi;
  }

  editNode(event: Event, node: FlowNode): void {
    this.flowService.editNode(node, this.#appendTo());
  }

  removeNode(event: Event, node: FlowNode): void {
    const selectedNodes = this.flowService.getSelectedNodes();
    // Delete the whole selection only when the clicked node is part of it; otherwise just the clicked node.
    const nodeIncluded = selectedNodes.some((selectedNode) => selectedNode.id === node.id);
    const nodesToRemove = selectedNodes.length && nodeIncluded ? selectedNodes : [node];

    const entity = this.flowService.translateService.instant('node.label', {
      count: nodesToRemove.length,
    });
    const header = this.flowService.translateService.instant('confirm.entity.delete.header', {
      entity,
    });
    const message = this.flowService.translateService.instant('confirm.flow.node.delete.confirmation', {
      count: nodesToRemove.length,
    });

    this.flowService.openConfirmDialog(header, message, async () => {
      for (const nodeToRemove of nodesToRemove) {
        await this.flowService.removeNode(nodeToRemove);
      }
    });
  }

  handleDoubleClick(event: Event): void {
    if (this.excludedDoubleClickNodes.includes(this.data.nodeData.type)) {
      return;
    }

    this.editNode(event, this.data);
  }
}
